// src/components/product/ReviewsTab.jsx
import { useState, useEffect } from "react";
import config from "../../config";

const { API_BASE } = config;

function formatDate(str) {
  return new Date(str).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

function Stars({ value }) {
  return (
    <span className="pdp-stars" aria-hidden="true">
      {"★".repeat(value)}{"☆".repeat(5 - value)}
    </span>
  );
}

export default function ReviewsTab({ productId, onSummaryChange }) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0, rating_counts: {} });
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/product-reviews.php?product_id=${productId}`)
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data.error) throw new Error(data.error);
        setReviews(data.reviews || []);
        const s = data.summary || { count: 0, average: 0, rating_counts: {} };
        setSummary(s);
        onSummaryChange?.(s);
      })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId]);

  if (loading) return <p className="pdp-reviews__loading">Loading reviews…</p>;
  if (error)   return <p className="pdp-reviews__error">Could not load reviews: {error}</p>;

  const maxCount = Math.max(1, ...Object.values(summary.rating_counts || {}));

  return (
    <div className="pdp-reviews">
      {summary.count > 0 && (
        <div className="pdp-reviews__summary">
          <div className="pdp-reviews__avg-block">
            <span className="pdp-reviews__avg">{summary.average.toFixed(1)}</span>
            <Stars value={Math.round(summary.average)} />
            <p className="pdp-reviews__count">Based on {summary.count} review{summary.count !== 1 ? "s" : ""}</p>
          </div>
          <div className="pdp-reviews__bars">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = summary.rating_counts?.[star] || 0;
              const pct = (count / maxCount) * 100;
              return (
                <div key={star} className="pdp-reviews__bar-row">
                  <span className="pdp-reviews__bar-label">{star}★</span>
                  <div className="pdp-reviews__bar-track">
                    <div className="pdp-reviews__bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="pdp-reviews__bar-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {reviews.length === 0 ? (
        <p className="pdp-reviews__empty">No reviews yet — be the first to review this product after your purchase.</p>
      ) : (
        <div className="pdp-reviews__list">
          {reviews.map(r => (
            <div key={r.id} className="pdp-review-card">
              <div className="pdp-review-card__head">
                <span className="pdp-review-card__name">{r.customer_name}</span>
                <span className="pdp-review-card__date">{formatDate(r.created_at)}</span>
              </div>
              <Stars value={r.rating} />
              <p className="pdp-review-card__content">{r.content}</p>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .pdp-stars { color: #f59e0b; letter-spacing: 1px; }
        .pdp-reviews__loading, .pdp-reviews__error { font-size: 14px; color: #6b7a99; padding: 12px 0; }
        .pdp-reviews__error { color: #991b1b; }
        .pdp-reviews__summary { display:flex; gap:32px; flex-wrap:wrap; padding:20px; background:#fafbfd; border-radius:12px; margin-bottom:24px; }
        .pdp-reviews__avg-block { display:flex; flex-direction:column; align-items:flex-start; gap:4px; min-width:140px; }
        .pdp-reviews__avg { font-size:36px; font-weight:800; color:#013d97; line-height:1; }
        .pdp-reviews__count { font-size:12.5px; color:#8895b0; margin-top:2px; }
        .pdp-reviews__bars { flex:1; min-width:200px; display:flex; flex-direction:column; gap:6px; justify-content:center; }
        .pdp-reviews__bar-row { display:flex; align-items:center; gap:8px; font-size:12px; color:#6b7a99; }
        .pdp-reviews__bar-label { width:26px; flex-shrink:0; }
        .pdp-reviews__bar-track { flex:1; height:6px; background:#e8ecf3; border-radius:4px; overflow:hidden; }
        .pdp-reviews__bar-fill { height:100%; background:#f59e0b; border-radius:4px; }
        .pdp-reviews__bar-count { width:22px; text-align:right; flex-shrink:0; }
        .pdp-reviews__empty { font-size:14px; color:#8895b0; padding:24px 0; }
        .pdp-reviews__list { display:flex; flex-direction:column; gap:16px; }
        .pdp-review-card { padding:16px 0; border-bottom:1px solid #f0f3f8; }
        .pdp-review-card:last-child { border-bottom:none; }
        .pdp-review-card__head { display:flex; justify-content:space-between; margin-bottom:4px; }
        .pdp-review-card__name { font-weight:700; font-size:14px; color:#0f1c38; }
        .pdp-review-card__date { font-size:12px; color:#8895b0; }
        .pdp-review-card__content { font-size:13.5px; color:#374151; line-height:1.6; margin-top:6px; }
      `}</style>
    </div>
  );
}